// Field crews open the capture screens on site, often with no signal, so the capture guard
// remembers the last person the server confirmed and lets them in while the phone is offline.
// Only who they are is kept (id, name, email, role), never a token: the server still decides
// every upload, and a remembered user is only allowed past the screen guard.

import { isRedirect, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { clearServiceWorkerApiCache } from "@/lib/pwa";
import { requireAuth } from "@/lib/route-guards";
import { SessionUnknownError } from "@/lib/session-cache";

export const OFFLINE_SESSION_KEY = "mesh.capture.offline-session";

const rememberedSchema = z.object({
  user: z.object({
    id: z.string().min(1),
    name: z.string(),
    email: z.string(),
    role: z.string().nullable(),
  }),
});

export type CaptureSession = z.infer<typeof rememberedSchema>;

type SessionStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

interface OfflineSessionDeps {
  /** May throw (private mode, blocked site data); that is treated as no storage. */
  storage: () => SessionStorage | null;
  isOnline: () => boolean;
  requireAuth: () => Promise<{
    user: { id: string; name: string; email: string; role?: string | null };
  }>;
}

function toLogin(): never {
  throw redirect({ to: "/login" });
}

export function createOfflineSession(deps: OfflineSessionDeps) {
  function store(): SessionStorage | null {
    try {
      return deps.storage();
    } catch {
      return null;
    }
  }

  function remember(session: CaptureSession) {
    try {
      store()?.setItem(OFFLINE_SESSION_KEY, JSON.stringify(session));
    } catch {
      // Full or blocked storage: the user is still in, they just cannot start offline.
    }
  }

  function remembered(): CaptureSession | null {
    try {
      const raw = store()?.getItem(OFFLINE_SESSION_KEY);
      if (!raw) return null;
      const parsed = rememberedSchema.safeParse(JSON.parse(raw));
      return parsed.success ? parsed.data : null;
    } catch {
      return null;
    }
  }

  function forget() {
    try {
      store()?.removeItem(OFFLINE_SESSION_KEY);
    } catch {
      // Nothing to clear if storage is blocked.
    }
  }

  /**
   * The capture screens' guard. Online it is `requireAuth()` and remembers who passed;
   * offline, or when the check cannot reach the server, it is the remembered user, else
   * sign in. A real "signed out" answer from the server forgets the remembered user.
   */
  async function requireCaptureAuth(): Promise<CaptureSession> {
    if (!deps.isOnline()) return remembered() ?? toLogin();
    let live: Awaited<ReturnType<OfflineSessionDeps["requireAuth"]>>;
    try {
      live = await deps.requireAuth();
    } catch (err) {
      if (isRedirect(err)) {
        forget();
        throw err;
      }
      // A fetch that never reached the server rejects with a TypeError; a check that got no
      // usable answer is a SessionUnknownError. Neither says the user signed out.
      if (err instanceof TypeError || err instanceof SessionUnknownError) {
        return remembered() ?? toLogin();
      }
      throw err;
    }
    const { id, name, email, role } = live.user;
    const session: CaptureSession = { user: { id, name, email, role: role ?? null } };
    remember(session);
    return session;
  }

  return { requireCaptureAuth, forget };
}

const browserSession = createOfflineSession({
  storage: () => (typeof localStorage === "undefined" ? null : localStorage),
  isOnline: () => typeof navigator === "undefined" || navigator.onLine !== false,
  requireAuth,
});

export const requireCaptureAuth = browserSession.requireCaptureAuth;

/** Sign-in and sign-out call this beside `forgetGuardSession`. */
export function forgetOfflineSession() {
  browserSession.forget();
  clearServiceWorkerApiCache();
}
